import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PLACE_TYPE_LABEL, STATUS_LABEL, type PlaceFeature } from '../../data/types';
import { useSelection } from '../../state/selection';
import { readFilters } from '../../state/url';
import { useAppData } from '../../state/AppData';

export function applyFilters(places: PlaceFeature[], params: URLSearchParams): PlaceFeature[] {
  const f = readFilters(params);
  return places.filter((p) =>
    (!f.type || p.properties.type === f.type) &&
    (!f.status || p.properties.currentStatus === f.status) &&
    (!f.precision || (f.precision === 'located') === Boolean(p.geometry)));
}

/** Lista equivalente al mapa (spec §8.4, §14): misma información, accesible sin cartografía. */
export function PlaceList({ places, withFilters = false }: { places: PlaceFeature[]; withFilters?: boolean }) {
  const [params, setParams] = useSearchParams();
  const sel = useSelection();
  const f = readFilters(params);
  const shown = withFilters ? applyFilters(places, params) : places;
  const setParam = (k: string, v: string) => setParams((p) => { const n = new URLSearchParams(p); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const types = [...new Set(places.map((p) => p.properties.type))];
  const { city } = useAppData();
  // en los capítulos de provincia la lista se agrupa por municipio: es la unidad del relato
  const groups = useMemo(() => {
    if (city.level !== 'provincia') return null;
    const m = new Map<string, PlaceFeature[]>();
    for (const p of shown) {
      const k = p.properties.municipality ?? '—';
      m.set(k, [...(m.get(k) ?? []), p]);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0], 'es'));
  }, [shown, city.level]);

  return (
    <section className="placelist" aria-labelledby="lista-lugares">
      <h2 id="lista-lugares" tabIndex={-1}>Lugares <span className="count">({shown.length})</span></h2>
      {withFilters && (
        <fieldset className="filters">
          <legend className="visually-hidden">Filtros</legend>
          <label>Tipo
            <select value={f.type ?? ''} onChange={(e) => setParam('tipo', e.target.value)}>
              <option value="">Todos</option>
              {types.map((t) => <option key={t} value={t}>{PLACE_TYPE_LABEL[t] ?? t}</option>)}
            </select>
          </label>
          <label>Estado actual
            <select value={f.status ?? ''} onChange={(e) => setParam('estado', e.target.value)}>
              <option value="">Todos</option>
              {Object.entries(STATUS_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </label>
          <label>Localización
            <select value={f.precision ?? ''} onChange={(e) => setParam('loc', e.target.value)}>
              <option value="">Todas</option>
              <option value="located">Con localización</option>
              <option value="unlocated">Sin localización</option>
            </select>
          </label>
        </fieldset>
      )}
      <ul className="placelist__items">
        {(groups ?? [['', shown] as [string, PlaceFeature[]]]).map(([mun, items]) => (
          <li key={mun || 'todos'} className={mun ? 'placelist__group' : undefined}>
            {mun && <h3 className="placelist__mun">{mun} <span className="count">({items.length})</span></h3>}
            <ul className="placelist__items">
              {items.map((p) => (
                <li key={p.id}>
                  <button type="button" className="placelist__btn" aria-pressed={sel.place === p.id} data-place={p.id} onClick={() => sel.selectPlace(p.id, 'list')}>
                    <span className="placelist__name">{p.properties.canonicalName}</span>
                    <span className="placelist__meta">
                      {PLACE_TYPE_LABEL[p.properties.type]} · {STATUS_LABEL[p.properties.currentStatus]} · {p.properties.mentionCount} menc.
                      {!p.geometry && <> · <em>sin localización</em></>}
                      {p.properties.geometryPrecision === 'approximate' && <> · <em>ubicación aproximada</em></>}
                      {/* en una localidad la precisión de término es la normal y no hace falta advertirla */}
                      {p.properties.geometryPrecision === 'municipality' && p.properties.type !== 'settlement'
                        && <> · <em>solo el término</em></>}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </li>
        ))}
        {shown.length === 0 && <li>Ningún lugar cumple los filtros.</li>}
      </ul>
    </section>
  );
}
